import React from "react";
import { formatIDR, formatDateID } from "@/lib/format";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, X } from "lucide-react";

export default function ReceiptDialog({ open, onOpenChange, pembayaran }) {
  if (!pembayaran) return null;

  const handlePrint = () => {
    const w = window.open("", "_blank", "width=420,height=600");
    if (!w) return;
    const css = `
      @page { size: 80mm auto; margin: 6mm; }
      * { box-sizing: border-box; }
      body { font-family: 'Manrope', -apple-system, sans-serif; color: #0f172a; padding: 0; margin: 0; }
      .wrap { padding: 12px; max-width: 320px; }
      h1 { font-family: 'Outfit', sans-serif; font-size: 16px; margin: 0; color: #047857; text-align: center; letter-spacing: 0.05em; }
      .sub { font-size: 11px; color: #475569; text-align: center; margin-top: 2px; }
      .hr { border-top: 1px dashed #94a3b8; margin: 10px 0; }
      .row { display: flex; justify-content: space-between; font-size: 12px; padding: 2px 0; }
      .row .l { color: #64748b; }
      .row .r { color: #0f172a; font-weight: 600; text-align: right; max-width: 60%; }
      .total { font-size: 14px; font-weight: 800; color: #047857; }
      .status { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
      .lunas { background: #d1fae5; color: #065f46; }
      .belum { background: #fee2e2; color: #991b1b; }
      .ft { text-align: center; font-size: 10px; color: #64748b; margin-top: 12px; }
      .nomor { text-align: center; font-family: 'Courier New', monospace; font-size: 11px; color: #475569; margin-top: 6px; }
    `;
    const isLunas = pembayaran.status === "lunas";
    w.document.write(`
      <!doctype html><html><head><meta charset="utf-8" />
      <title>Bukti ${pembayaran.nomor || ""}</title>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@600;700;800&family=Manrope:wght@400;600;700&display=swap" rel="stylesheet" />
      <style>${css}</style></head><body>
        <div class="wrap">
          <h1>SiMadin</h1>
          <div class="sub">Madrasah Al Islamiyyah Sunan Giri</div>
          <div class="sub">Surabaya</div>
          <div class="hr"></div>
          <div class="sub" style="font-weight:700; color:#0f172a;">BUKTI PEMBAYARAN KITAB</div>
          <div class="nomor">${pembayaran.nomor || ""}</div>
          <div class="hr"></div>
          <div class="row"><span class="l">Tanggal</span><span class="r">${formatDateID(pembayaran.tanggal_bayar)}</span></div>
          <div class="row"><span class="l">Nama Santri</span><span class="r">${pembayaran.santri?.nama || "-"}</span></div>
          <div class="row"><span class="l">NIS</span><span class="r">${pembayaran.santri?.nis || "-"}</span></div>
          <div class="row"><span class="l">Kelas</span><span class="r">${pembayaran.kelas?.nama || "-"}</span></div>
          <div class="hr"></div>
          <div class="row"><span class="l">Kitab</span><span class="r">${pembayaran.kitab?.nama || "-"}</span></div>
          <div class="row"><span class="l">Tahun Ajaran</span><span class="r">${pembayaran.tahun_ajaran || "-"}</span></div>
          <div class="row"><span class="l">Status</span><span class="r"><span class="status ${isLunas ? "lunas" : "belum"}">${isLunas ? "LUNAS" : "BELUM LUNAS"}</span></span></div>
          ${pembayaran.keterangan ? `<div class="row"><span class="l">Catatan</span><span class="r" style="font-weight:400;">${pembayaran.keterangan}</span></div>` : ""}
          <div class="hr"></div>
          <div class="row total"><span>Total</span><span>${formatIDR(pembayaran.harga_snapshot)}</span></div>
          <div class="hr"></div>
          <div class="ft">Petugas: ${pembayaran.kasir_name || "-"}</div>
          <div class="ft">Terima kasih atas pembayaran Anda.</div>
        </div>
        <script>window.onload=function(){window.print();}</script>
      </body></html>
    `);
    w.document.close();
  };

  const isLunas = pembayaran.status === "lunas";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogTitle>Bukti Pembayaran</DialogTitle>
        <DialogDescription>Pratinjau bukti pembayaran kitab.</DialogDescription>
        <div className="rounded-xl border border-slate-200 bg-white p-5 mt-2 font-body">
          <div className="text-center">
            <div className="font-heading font-bold text-emerald-700 text-lg">SiMadin</div>
            <div className="text-xs text-slate-500">Madrasah Al Islamiyyah Sunan Giri — Surabaya</div>
          </div>
          <div className="my-3 border-t border-dashed border-slate-300" />
          <div className="text-center text-xs font-bold tracking-widest text-slate-700">BUKTI PEMBAYARAN KITAB</div>
          <div className="text-center text-[11px] font-mono text-slate-500 mt-1">{pembayaran.nomor}</div>
          <div className="my-3 border-t border-dashed border-slate-300" />
          <div className="space-y-1 text-sm">
            <Row l="Tanggal" r={formatDateID(pembayaran.tanggal_bayar)} />
            <Row l="Nama Santri" r={pembayaran.santri?.nama} />
            <Row l="NIS" r={pembayaran.santri?.nis} />
            <Row l="Kelas" r={pembayaran.kelas?.nama} />
            <div className="my-2 border-t border-dashed border-slate-300" />
            <Row l="Kitab" r={pembayaran.kitab?.nama} />
            <Row l="Tahun Ajaran" r={pembayaran.tahun_ajaran} />
            <Row l="Status" r={
              <span className={`px-2 py-0.5 text-[10px] rounded-full font-semibold tracking-wider ${
                isLunas ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
              }`}>{isLunas ? "LUNAS" : "BELUM LUNAS"}</span>
            } />
            {pembayaran.keterangan && <Row l="Catatan" r={pembayaran.keterangan} />}
          </div>
          <div className="my-3 border-t border-dashed border-slate-300" />
          <div className="flex justify-between items-center font-bold text-emerald-700">
            <span className="text-sm">Total</span>
            <span className="text-lg tabular-nums">{formatIDR(pembayaran.harga_snapshot)}</span>
          </div>
          <div className="mt-4 text-center text-xs text-slate-500">Petugas: {pembayaran.kasir_name || "-"}</div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="receipt-close">
            <X className="h-4 w-4 mr-1" /> Tutup
          </Button>
          <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700" data-testid="receipt-print-button">
            <Printer className="h-4 w-4 mr-1" /> Cetak
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ l, r }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500">{l}</span>
      <span className="text-slate-900 font-semibold text-right">{r || "-"}</span>
    </div>
  );
}
